/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import React, { lazy } from 'react';
import { Redirect } from 'react-router-dom';

import AuthLayout from './layouts/Auth';
import ErrorLayout from './layouts/Error';
import DashboardLayout from './layouts/Dashboard';
import PageLayout from './layouts/Page';

import { appRoutes } from './constants';

const routes = [
  {
    path: appRoutes.HOME_PAGE,
    exact: true,
    component: () => <Redirect to={appRoutes.COMPETITIONS_PAGE} />,
  },
  {
    // The public TV. Top-level and layout-free ON PURPOSE: a screen has no
    // user, no menu and no navigation, and must render from a URL alone —
    // including after a power cut with nobody in the hall. Placed before the
    // authenticated layouts so it is never wrapped by a guard.
    path: appRoutes.PUBLIC_DISPLAY_PAGE,
    exact: true,
    component: lazy(() => import('views/PublicDisplay')),
  },
  {
    // The kiosk entry point. Layout-free for the same reasons as the display
    // itself, and exact so it never shadows /display/:token.
    path: appRoutes.DISPLAY_PAIRING_PAGE,
    exact: true,
    component: lazy(() => import('views/DisplayPairing')),
  },
  {
    // A PHONE AT THE MAT (6 Oct 2026). Layout-free and outside the guards for
    // the same reasons as a screen: it pairs by code and holds a device token,
    // not a login. Exact, so the pairing page never shadows the bout page.
    path: appRoutes.MAT_PHONE_PAIRING_PAGE,
    exact: true,
    component: lazy(() => import('views/MatPhone/MatPhonePairing')),
  },
  {
    path: appRoutes.MAT_PHONE_PAGE,
    exact: true,
    component: lazy(() => import('views/MatPhone')),
  },
  {
    // A participant's phone — the QR code at the hall's entrance (29 Oct
    // 2026). Top-level and layout-free like the display: no user, no menu.
    path: appRoutes.PARTICIPANT_MONITOR_PAGE,
    exact: true,
    component: lazy(() => import('views/ParticipantMonitor')),
  },
  {
    // The published schedule (29 Oct 2026): anybody with the link, no login —
    // top-level and layout-free like the participant's phone.
    path: appRoutes.PUBLIC_SCHEDULE_PAGE,
    exact: true,
    component: lazy(() => import('views/PublicSchedule')),
  },
  {
    path: appRoutes.AUTH,
    component: AuthLayout,
    routes: [
      {
        path: appRoutes.AUTH_LOGIN_PAGE,
        exact: true,
        component: lazy(() => import('views/Login')),
      },
      {
        path: appRoutes.AUTH_REGISTER_PAGE,
        exact: true,
        component: lazy(() => import('views/Register'))
      },
      // PASSWORD RECOVERY. Under /auth so both pages inherit AuthLayout, and
      // reachable without a session — somebody who has forgotten their
      // password cannot be asked to log in first.
      {
        path: appRoutes.AUTH_FORGOT_PASSWORD_PAGE,
        exact: true,
        component: lazy(() => import('views/ForgotPassword'))
      },
      {
        path: appRoutes.AUTH_RESET_PASSWORD_PAGE,
        exact: true,
        component: lazy(() => import('views/ResetPassword'))
      },
      // The confirmation link from an email-change message. Anonymous for the
      // same reason as the two above — the mail client has no session.
      {
        path: appRoutes.AUTH_CONFIRM_EMAIL_PAGE,
        exact: true,
        component: lazy(() => import('views/ConfirmEmail'))
      },
      {
        component: () => <Redirect to={appRoutes.ERROR_PAGE_404} />,
      },
    ]
  },
  {
    path: appRoutes.ERROR,
    component: ErrorLayout,
    routes: [
      {
        path: appRoutes.ERROR_PAGE_401,
        exact: true,
        component: lazy(() => import('views/Error401'))
      },
      {
        path: appRoutes.ERROR_PAGE_404,
        exact: true,
        component: lazy(() => import('views/Error404'))
      },
      {
        path: appRoutes.ERROR_PAGE_500,
        exact: true,
        component: lazy(() => import('views/Error500'))
      },
      {
        component: () => <Redirect to={appRoutes.ERROR_PAGE_404} />
      },
    ]
  },
  {
    path: appRoutes.PAGES,
    component: PageLayout,
    routes: [
      {
        path: appRoutes.PAGE_TERMS_AND_CONDITIONS,
        exact: true,
        component: lazy(() => import('views/Pages/TermsAndConditions'))
      },
      {
        component: () => <Redirect to={appRoutes.ERROR_PAGE_404} />
      },
    ]
  },
  {
    route: '*',
    component: DashboardLayout,
    routes: [
      {
        path: appRoutes.COMPETITIONS_PAGE,
        exact: true,
        component: lazy(() => import('views/Competitions'))
      },
      {
        // Detail must come after the exact list route; itself exact so it
        // never shadows /schedule etc.
        path: appRoutes.COMPETITION_DETAIL_PAGE,
        exact: true,
        component: lazy(() => import('views/CompetitionDetail'))
      },
      {
        // The chief secretary's floor: every mat of the competition they run.
        path: appRoutes.COMPETITION_OPERATIONS_PAGE,
        exact: true,
        component: lazy(() => import('views/CompetitionOperations'))
      },
      {
        path: appRoutes.COMPETITION_DEVICES_PAGE,
        exact: true,
        component: lazy(() => import('views/CompetitionDevices'))
      },
      {
        path: appRoutes.COMPETITION_MONITOR_PAGE,
        exact: true,
        component: lazy(() => import('views/CompetitionMonitor'))
      },
      {
        // ONE mat. Deliberately its own route rather than a tab: a tatami
        // secretary works here and nowhere else, and the backend refuses a
        // mat they are not assigned to — changing the number in the URL does
        // not widen what they can see.
        path: appRoutes.TATAMI_WORKSPACE_PAGE,
        exact: true,
        component: lazy(() => import('views/TatamiWorkspace'))
      },
      {
        // THE competition-scoped Configuration tab — the one authoritative
        // editor for execution format / draw strategy / competition level.
        // Competition-scoped on purpose: the id is in the URL, so there is
        // no ambiguity about which competition is being configured.
        path: appRoutes.COMPETITION_CONFIGURATION_PAGE,
        exact: true,
        component: lazy(() => import('views/CompetitionSettingsDetail'))
      },
      {
        // «Мої татамі»: a secretary's way from the menu to their own mat.
        path: appRoutes.MY_TATAMIS_PAGE,
        exact: true,
        component: lazy(() => import('views/MyTatamis'))
      },
      {
        // Every report of ONE competition in one place, plus its blank forms.
        path: appRoutes.COMPETITION_REPORTS_PAGE,
        exact: true,
        component: lazy(() => import('views/CompetitionReports'))
      },
      {
        path: appRoutes.APPLICATIONS_PAGE,
        exact: true,
        component: lazy(() => import('views/Applications'))
      },
      {
        path: appRoutes.PROFILES_PAGE,
        exact: true,
        component: lazy(() => import('views/Profiles'))
      },
      {
        path: appRoutes.SPORTSMEN_PAGE,
        exact: true,
        component: lazy(() => import('views/Sportsmen'))
      },
      {
        path: appRoutes.ACCOUNT_PAGE,
        exact: true,
        component: lazy(() => import('views/Account'))
      },
      {
        path: appRoutes.AUDIT_LOG_PAGE,
        exact: true,
        component: lazy(() => import('views/AuditLog'))
      },
      {
        path: appRoutes.PROFILE_DUPLICATES_PAGE,
        exact: true,
        component: lazy(() => import('views/ProfileDuplicates'))
      },
      {
        path: appRoutes.ACCOUNT_PAGE_TAB,
        exact: true,
        component: lazy(() => import('views/Account'))
      },
      {
        path: appRoutes.COMPETITION_SETTINGS_PAGE,
        exact: true,
        component: lazy(() => import('views/CompetitionSettings'))
      },
      {
        path: appRoutes.COMPETITION_SETTINGS_PAGE_TAB,
        exact: true,
        component: lazy(() => import('views/CompetitionSettings'))
      },
      {
        path: appRoutes.SCHEDULE_PAGE,
        exact: true,
        component: lazy(() => import('views/Schedule'))
      },
      {
        path: appRoutes.SHOOTING_PAGE,
        exact: true,
        component: lazy(() => import('views/ShootingProtocol'))
      },
      {
        path: appRoutes.ATTENDANCE_PAGE,
        exact: true,
        component: lazy(() => import('views/AttendanceCheckIn'))
      },
      {
        // The route the attendance page used to live at — redirected, not
        // reimplemented, so there is only ever ONE attendance page.
        path: appRoutes.LEGACY_ATTENDANCE_PAGE,
        exact: true,
        component: props => (
          <Redirect to={`/competitions/${props.match.params.competitionId}/attendance`} />
        ),
      },
      {
        path: appRoutes.MSAC_FINAL_PAGE,
        exact: true,
        component: lazy(() => import('views/MsacFinalProtocol'))
      },
      {
        component: () => <Redirect to={appRoutes.ERROR_PAGE_404} />
      },
    ]
  },
];

export default routes;
